import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiClient } from "@/App";
import { resolveMediaUrl } from "@/lib/media-url";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  ChatCircle,
  Heart,
  Code,
  Trophy,
  Star,
  CopySimple,
  ShareNetwork,
} from "@phosphor-icons/react";
import CodeSandbox from "@/components/CodeSandbox";

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function setMetaTag(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

const SHARE_TARGETS = [
  {
    label: "WhatsApp",
    build: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  {
    label: "Telegram",
    build: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    label: "Facebook",
    build: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    label: "LinkedIn",
    build: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

export default function PublicPostDetailPage() {
  // Supports both /post/public/:postId (legacy) and /showcase/post/:slug
  const { postId: legacyId, slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let postRes;
      if (slug) {
        try {
          postRes = await apiClient.get(`/posts/public/slug/${slug}`);
        } catch (_) {
          // Graceful fallback: treat the param as a legacy post id
          postRes = await apiClient.get(`/posts/public/${slug}`);
        }
      } else {
        postRes = await apiClient.get(`/posts/public/${legacyId}`);
      }
      const commentsRes = await apiClient.get(`/posts/public/${postRes.data.id}/comments`);
      setPost(postRes.data);
      setComments(commentsRes.data || []);
    } catch (error) {
      toast.error("Post not found");
      navigate("/feed/public", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, legacyId, slug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const canonicalUrl = post ? `${window.location.origin}/showcase/post/${post.slug || post.id}` : "";

  // Best-effort Open Graph metadata (client-rendered SPA)
  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | DIIT Showcase`;
    const firstImage = post.media_urls?.find((u) => /\.(jpg|jpeg|png|gif|webp)$/i.test(u));
    setMetaTag("property", "og:title", post.title);
    setMetaTag("property", "og:description", (post.description || "").slice(0, 200));
    setMetaTag("property", "og:type", "article");
    setMetaTag("property", "og:url", canonicalUrl);
    if (firstImage) {
      setMetaTag("property", "og:image", new URL(resolveMediaUrl(firstImage), window.location.origin).href);
    }
    return () => {
      document.title = "DIIT Showcase";
    };
  }, [post, canonicalUrl]);

  const handleShareTarget = async (target) => {
    try {
      apiClient.post(`/posts/${post.id}/share`).catch(() => {});
    } catch (_) {}
    window.open(target.build(canonicalUrl, post.title), "_blank", "noopener,noreferrer");
  };

  const handleNativeShare = async () => {
    try {
      apiClient.post(`/posts/${post.id}/share`).catch(() => {});
    } catch (_) {}
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, url: canonicalUrl });
      } catch (_) {}
    } else {
      await navigator.clipboard.writeText(canonicalUrl);
      toast.success("Link copied");
    }
  };

  const handleCopyLink = async () => {
    try {
      apiClient.post(`/posts/${post.id}/share`).catch(() => {});
    } catch (_) {}
    await navigator.clipboard.writeText(canonicalUrl);
    toast.success("Link copied to clipboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) return null;

  const hasCode = post.html_code || post.css_code || post.js_code;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/90 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-surface transition-colors"
            data-testid="public-post-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold truncate flex-1 min-w-0">{post.title}</h1>
          <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center justify-end gap-2">
            <Button asChild variant="outline" className="rounded-full" data-testid="public-post-feed-link">
              <Link to="/feed/public">Public Feed</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="public-post-leaderboard-link">
              <Link to="/leaderboard/public">
                <Trophy className="w-4 h-4 mr-2" />
                Leaderboard
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <article className="bg-surface rounded-xl border border-border/40 overflow-hidden">
          <div className="p-6 border-b border-border/40">
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-lg font-bold text-background">
                {post.author_name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-semibold">{post.author_name}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-0 sm:ml-auto">
                {post.is_pinned && (
                  <span className="text-xs px-2 py-1 rounded-full bg-warning/20 text-warning">Pinned</span>
                )}
                {post.is_featured && !post.is_pinned && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                    <Star weight="fill" className="w-3.5 h-3.5" /> Featured
                  </span>
                )}
                {!post.is_pinned && !post.is_featured && (
                  <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">Public</span>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <h2 className="font-heading text-2xl font-bold mb-3">{post.title}</h2>
              <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">{post.description}</p>
            </div>

            {/* Share bar */}
            <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-surface-elevated border border-border/40">
              <span className="text-xs font-medium text-muted-foreground mr-1">Share:</span>
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-surface border border-border hover:border-primary transition-colors"
                data-testid="share-copy-link"
              >
                <CopySimple className="w-3.5 h-3.5" /> Copy Link
              </button>
              {SHARE_TARGETS.map((target) => (
                <button
                  key={target.label}
                  onClick={() => handleShareTarget(target)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-surface border border-border hover:border-primary transition-colors"
                  data-testid={`share-${target.label.toLowerCase()}`}
                >
                  {target.label}
                </button>
              ))}
              <button
                onClick={handleNativeShare}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary text-primary-foreground"
                data-testid="share-native"
              >
                <ShareNetwork className="w-3.5 h-3.5" /> More
              </button>
            </div>

            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {post.media_urls?.length > 0 && (
              <div className="grid gap-4">
                {post.media_urls.map((url, idx) => {
                  const isVideo = url.match(/\.(mp4|webm|mov)$/i);
                  const isAudio = url.match(/\.(mp3|wav|ogg)$/i);

                  if (isVideo) {
                    return (
                      <div key={idx} className="rounded-lg overflow-hidden bg-black/70 border border-border/30">
                        <video
                          src={resolveMediaUrl(url)}
                          controls
                          playsInline
                          preload="metadata"
                          className="w-full h-auto max-h-[65vh] object-contain bg-black"
                        />
                      </div>
                    );
                  }

                  if (isAudio) {
                    return (
                      <audio
                        key={idx}
                        src={resolveMediaUrl(url)}
                        controls
                        preload="none"
                        className="w-full"
                      />
                    );
                  }

                  return (
                    <div key={idx} className="rounded-lg overflow-hidden bg-black/70 border border-border/30">
                      <img
                        src={resolveMediaUrl(url)}
                        alt={`Media ${idx + 1}`}
                        loading="lazy"
                        className="w-full h-auto max-h-[70vh] object-contain bg-black"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {hasCode && (
              <div className="rounded-xl overflow-hidden border border-border/40">
                <div className="bg-surface-elevated p-4 border-b border-border/40 flex items-center gap-2">
                  <Code className="w-5 h-5 text-accent" />
                  <span className="font-mono text-sm font-medium">Live Preview</span>
                </div>
                <CodeSandbox html={post.html_code || ""} css={post.css_code || ""} js={post.js_code || ""} />
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-border/40 flex items-center gap-6 text-muted-foreground">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5" />
              <span>{post.likes_count}</span>
            </div>
            <div className="flex items-center gap-2">
              <ChatCircle className="w-5 h-5" />
              <span>{post.comments_count} comments</span>
            </div>
          </div>
        </article>

        <section className="mt-6">
          <h3 className="font-heading font-semibold text-lg mb-4">Comments</h3>
          {comments.length === 0 ? (
            <div className="bg-surface rounded-xl border border-border/40 p-6 text-center">
              <p className="text-muted-foreground">No comments yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {comments.map((comment) => (
                <div key={comment.id} className="bg-surface rounded-xl border border-border/40 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-bold text-background">
                      {comment.author_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-sm">{comment.author_name}</p>
                        <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-foreground/90 text-sm leading-relaxed whitespace-pre-wrap">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="mt-6 text-center bg-surface rounded-xl border border-border/40 p-5">
          <p className="text-muted-foreground mb-3">Want to like, comment, and publish your own projects?</p>
          <div className="flex items-center justify-center gap-3">
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/login">Login</Link>
            </Button>
            <Button asChild className="rounded-full">
              <Link to="/register">Create Account</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
